<template>
    <div class="pdf-renderer flex flex-col h-full">
        <div v-if="pdf" class="flex gap-1 p-2">
            <UButton
                class="max-w-[calc(100%-70px)]"
                icon="i-ph-download"
                data-cy="document-preview-download-button"
                variant="outline"
                color="neutral"
                @click="$emit('onClickDownload')"
            >
                <span class="truncate" :data-cy="`document-preview-filename-${documentMeta.uuid}`">{{ documentMeta.filename }}</span>
            </UButton>
            <UButton
                icon="i-ph-magnifying-glass-minus"
                variant="outline"
                color="neutral"
                @click="zoomOut"
            />
            <!--            <span class="flex items-center text-sm">{{ (zoomMultiplier * 100).toFixed(0) }} %</span>-->
            <UButton
                icon="i-ph-magnifying-glass-plus"
                variant="outline"
                color="neutral"
                @click="zoomIn"
            />
            <!--            <UButton-->
            <!--                variant="outline"-->
            <!--                color="neutral"-->
            <!--                label="100%"-->
            <!--                @click="resetZoom"-->
            <!--            />-->
        </div>

        <div ref="containerRef" class="m-2 flex-1 min-h-0 overflow-auto">
            <!--
                Wrapper width drives fit-parent scale: fit-parent measures its direct parent's
                clientWidth, so setting width = containerWidth × zoom gives the correct scale
                at every zoom level. :scale="renderTrigger" is ignored by fit-parent as a value
                but changing it triggers VuePDF to re-render and re-measure.
            -->
            <div
                class="flex flex-col gap-2"
                :style="{ width: renderTrigger + 'px' }"
            >
                <template v-if="pdf && containerWidth > 0">
                    <VuePDF
                        v-for="page in pages"
                        ref="pdfPages"
                        :key="page"
                        :pdf="pdf"
                        :page="page"
                        fit-parent
                        text-layer
                        annotation-layer
                        :scale="renderTrigger"
                    />
                </template>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, useTemplateRef } from 'vue'
import {usePDF, VuePDF} from '@tato30/vue-pdf'
import '@tato30/vue-pdf/style.css'
import {EnrichedDocument} from '@/types/transaction/DocumentMeta'
import hotkeys from 'hotkeys-js'

const container = useTemplateRef<HTMLDivElement>('containerRef')
const pdfPages = ref(null)
const containerWidth = ref(0)
const zoomMultiplier = ref(1.0)

// fit-parent ignores :scale as a value but re-renders when it changes —
// so this double-duties as both the wrapper width and the re-render trigger.
const renderTrigger = computed(() => containerWidth.value * zoomMultiplier.value)

let resizeObserver: ResizeObserver | null = null

const props = defineProps({
    documentMeta: {
        type: Object as () => EnrichedDocument,
        required: true
    }
})

defineEmits<{ (e: 'onClickDownload'): Promise<void> }>()

const {pdf, pages} = usePDF(props.documentMeta.url)

const cleanup = () => {
    zoomMultiplier.value = 1.0
}

const zoomIn = () => {
    if (zoomMultiplier.value < 3.0) {
        zoomMultiplier.value = Math.round((zoomMultiplier.value + 0.1) * 10) / 10
    }
}
const zoomOut = () => {
    if (zoomMultiplier.value > 0.25) {
        zoomMultiplier.value = Math.round((zoomMultiplier.value - 0.1) * 10) / 10
    }
}
const resetZoom = () => {
    zoomMultiplier.value = 1.0
}

const getParentScroller = () => {
    const renderer = document.getElementsByClassName('pdf-renderer')
    if (renderer.length > 0) {
        return renderer[0].parentElement
    }
}

const relativeScrollAmount = () => {
    return 100 / zoomMultiplier.value
}

const scrollDown = () => {
    const parentScroller = getParentScroller()
    if (parentScroller) {
        parentScroller.scrollBy({top: relativeScrollAmount(), behavior: 'smooth'})
    }
}

const scrollUp = () => {
    const parentScroller = getParentScroller()
    if (parentScroller) {
        parentScroller.scrollBy({top: -relativeScrollAmount(), behavior: 'smooth'})
    }
}

const scrollTop = () => {
    const parentScroller = getParentScroller()
    if (parentScroller) {
        parentScroller.scrollTo({top: 0, behavior: 'smooth'})
    }
}

const scrollLeft = () => {
    const parentScroller = getParentScroller()
    if (parentScroller) {
        parentScroller.scrollBy({left: -relativeScrollAmount(), behavior: 'smooth'})
    }
}

const scrollRight = () => {
    const parentScroller = getParentScroller()
    if (parentScroller) {
        parentScroller.scrollBy({left: relativeScrollAmount(), behavior: 'smooth'})
    }
}

//TODO: Move bindinds to app/body level, and stop propagation, so we can capture before we hit input fields
hotkeys('alt+shift+j', {capture: true}, scrollUp)
hotkeys('alt+shift+k', {capture: true}, scrollDown)
hotkeys('alt+shift+g', {capture: true}, scrollTop)

hotkeys('alt+shift+h', {capture: true}, scrollLeft)
hotkeys('alt+shift+l', {capture: true}, scrollRight)

hotkeys('alt+shift+y', {capture: true}, zoomOut)
hotkeys('alt+shift+u', {capture: true}, zoomIn)
hotkeys('alt+shift+i', {capture: true}, resetZoom)

onMounted(() => {
    if (container.value) {
        containerWidth.value = container.value.clientWidth

        let resizeTimer: ReturnType<typeof setTimeout> | null = null
        resizeObserver = new ResizeObserver(() => {
            if (resizeTimer) clearTimeout(resizeTimer)
            resizeTimer = setTimeout(() => {
                containerWidth.value = container.value!.clientWidth
            }, 50)
        })
        resizeObserver.observe(container.value)
    }
})

onUnmounted(() => {
    resizeObserver?.disconnect()
    cleanup()
})
</script>

<style scoped>

</style>